import type { ErrorBody } from './types';

/** Header carrying the session CSRF token on every state-changing request. */
export const CSRF_HEADER = 'X-CSRF-Token';

/** A non-2xx response, carrying the server's stable error code. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string> | undefined;

  constructor(status: number, code: string, message: string, fields?: Record<string, string>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

/**
 * True for the 401 that means the session is gone (expired, signed out
 * elsewhere). Other 401 codes (invalid_code, invalid_credentials,
 * invalid_token) refuse one request and leave the session as it was.
 */
export function isSessionEnded(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401 && err.code === 'unauthenticated';
}

export interface ClientOptions {
  /** Path prefix of the API on this host, e.g. "/api". */
  baseUrl: string;
  fetch?: typeof fetch;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Typed JSON client for one host. Cookies ride along (credentials: include);
 * the CSRF token is kept in memory, set from login and /auth/me responses,
 * and sent on every non-GET request. Before a session exists any non-empty
 * value satisfies the server, so "pending" is sent.
 */
export class Client {
  private csrf = '';
  private readonly baseUrl: string;
  private readonly doFetch: typeof fetch;

  constructor(opts: ClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, '');
    this.doFetch = opts.fetch ?? ((...a) => fetch(...a));
  }

  setCsrfToken(token: string): void {
    this.csrf = token;
  }

  get<T>(path: string, query?: Record<string, string | number | boolean | undefined>) {
    return this.request<T>('GET', withQuery(path, query));
  }
  post<T>(path: string, body?: unknown) {
    return this.request<T>('POST', path, body);
  }
  put<T>(path: string, body?: unknown) {
    return this.request<T>('PUT', path, body);
  }
  patch<T>(path: string, body?: unknown) {
    return this.request<T>('PATCH', path, body);
  }
  delete<T>(path: string) {
    return this.request<T>('DELETE', path);
  }

  /**
   * The absolute path of an API route, for a link the browser follows
   * itself (a PDF download): it sends the session cookie, and a GET needs
   * no CSRF token. Query values that are undefined or '' are left out.
   */
  url(path: string, query?: Record<string, string | number | boolean | undefined>): string {
    return this.baseUrl + withQuery(path, query);
  }

  /**
   * GET a download: its body as text and the filename from
   * Content-Disposition. Errors throw ApiError as request does.
   */
  async getFile(path: string): Promise<DownloadedFile> {
    const res = await this.doFetch(this.baseUrl + path, {
      method: 'GET',
      headers: { Accept: '*/*' },
      credentials: 'include',
    });
    const text = await res.text();
    if (!res.ok) throw apiError(res, parseJSON(text));
    return { text, filename: dispositionFilename(res.headers.get('Content-Disposition')) };
  }

  /**
   * PUT a file's bytes to url, a path on this host from its root (the
   * uploadUrl of an upload reservation), with headers and the CSRF token.
   * Content-Length is the browser's to set from the body, so it is never
   * sent from headers. A url on another origin is refused before any
   * request: it would carry the session cookie and CSRF token there.
   */
  async upload(url: string, body: Blob, headers: Record<string, string> = {}): Promise<void> {
    if (!url.startsWith('/') || url.startsWith('//'))
      throw new ApiError(0, 'bad_upload_url', 'upload URL is not on this host');
    const h: Record<string, string> = { Accept: 'application/json' };
    for (const [k, v] of Object.entries(headers))
      if (k.toLowerCase() !== 'content-length') h[k] = v;
    h[CSRF_HEADER] = this.csrf || 'pending';
    const res = await this.doFetch(url, {
      method: 'PUT',
      headers: h,
      credentials: 'include',
      body,
    });
    if (res.ok) return;
    throw apiError(res, parseJSON(await res.text()));
  }

  async request<T>(method: Method, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (method !== 'GET') headers[CSRF_HEADER] = this.csrf || 'pending';
    const res = await this.doFetch(this.baseUrl + path, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const data: unknown = text ? JSON.parse(text) : undefined;
    if (!res.ok) throw apiError(res, data);
    const token = (data as { csrfToken?: unknown } | undefined)?.csrfToken;
    if (typeof token === 'string') this.csrf = token;
    return data as T;
  }
}

/** A downloaded file: its text and, when the server named it, its filename. */
export interface DownloadedFile {
  text: string;
  filename?: string;
}

/** The filename parameter of a Content-Disposition header, quoted or bare. */
export function dispositionFilename(header: string | null): string | undefined {
  const m = header && /filename="([^"]*)"|filename=([^;\s]+)/i.exec(header);
  return m ? (m[1] ?? m[2]) : undefined;
}

function apiError(res: Response, data: unknown): ApiError {
  const err = (data as ErrorBody | undefined)?.error;
  return new ApiError(
    res.status,
    err?.code ?? 'http_' + res.status,
    err?.message ?? res.statusText,
    err?.fields,
  );
}

/** The parsed body, or undefined when it is empty or not JSON. */
function parseJSON(text: string): unknown {
  try {
    return text ? JSON.parse(text) : undefined;
  } catch {
    return undefined;
  }
}

function withQuery(path: string, query?: Record<string, string | number | boolean | undefined>) {
  if (!query) return path;
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}
